export const PAGE_PATH = "/what-is-classroom-automation";

export const PAGE_SEO = {
  title: "What Is Classroom Automation? A Complete Guide for Teachers",
  description:
    "Learn how Classroom Automation helps teachers automate routines, timers, transitions, reminders, and classroom management tasks. See how Lesson Launchpad creates automated classroom experiences.",
  path: PAGE_PATH,
  ogType: "article",
};

export const TABLE_OF_CONTENTS = [
  { id: "mental-load", label: "The Hidden Mental Load of Teaching" },
  { id: "definition", label: "What Is Classroom Automation?" },
  { id: "why-it-matters", label: "Why Classroom Automation Matters" },
  { id: "examples", label: "Examples of Classroom Automation" },
  { id: "routine-builder", label: "The Automated Routine Builder" },
  { id: "day-in-class", label: "A Day in an Automated Classroom" },
  { id: "vs-traditional", label: "Classroom Automation vs Traditional Tools" },
  { id: "founder-story", label: "Built by a Teacher Who Needed This" },
  { id: "faq", label: "Frequently Asked Questions" },
];

export const HERO = {
  headline: "What Is Classroom Automation?",
  subheadline:
    "Teachers spend hundreds of small moments every day managing routines, transitions, timers, reminders, schedules, and classroom procedures.",
  subheadlineContinued:
    "Classroom Automation allows those moments to happen automatically, so teachers can spend less time managing and more time teaching.",
  supportingParagraphs: [
    "For decades, teachers have accepted many classroom management tasks as unavoidable.",
    "Starting timers. Repeating directions. Managing transitions. Displaying reminders. Updating screens. Remembering everything.",
    "What if your classroom could handle those tasks for you?",
  ],
  primaryCta: "Start Free Trial",
  secondaryCta: "Watch Classroom Automation in Action",
  videoLabel:
    "Video needed: A complete school day running automatically through Lesson Launchpad. Teacher rarely touches the computer. Timers, reminders, transitions, music, countdowns, and content changes happen automatically.",
  videoCaption: "Nothing was clicked.",
};

export const MENTAL_LOAD = {
  headline: "The Hidden Mental Load of Teaching",
  intro:
    "Ask any teacher what their day feels like, and you'll hear the same story: teaching is only part of the job. The rest is an endless stream of small decisions that never stop.",
  painPoints: [
    {
      title: "Repeating directions",
      copy: "You explain the warm-up. Three students walk in late. You explain it again. Two more ask what page. By the time everyone starts, five minutes are gone, and you haven't taught anything yet.",
    },
    {
      title: "Monitoring the clock",
      copy: "Glance at the wall clock. Glance at your watch. Calculate how many minutes are left. Wonder if you should extend the activity or cut it short. Repeat every few minutes until the bell.",
    },
    {
      title: "Remembering reminders",
      copy: "Picture day is Thursday. Field trip forms are due Friday. The assembly starts at 1:15. You wrote sticky notes, set phone alarms, and told yourself you'd remember, then forgot during third period.",
    },
    {
      title: "Launching timers",
      copy: "Open a new tab. Search for a countdown timer. Set the time. Go fullscreen. Realize you need to adjust it. Start over. Meanwhile, students are waiting, and your lesson momentum is gone.",
    },
    {
      title: "Transitioning between activities",
      copy: "Cleanup time. Switch slides. Queue music. Update the agenda. Tell students what's next. Watch the clock. Start the next timer. Every transition is a mini production you run manually.",
    },
    {
      title: "Preparing students for deadlines",
      copy: "Tests tomorrow. Projects due next week. Early dismissal for athletes. You know these reminders matter, but delivering them at the right moment without derailing your lesson takes constant vigilance.",
    },
    {
      title: "Keeping routines consistent",
      copy: "Monday ran smoothly. Tuesday you forgot the bell work timer. Wednesday the agenda wasn't updated. Thursday felt chaotic. Students notice inconsistency, and so do you.",
    },
  ],
  statBlock: {
    headline: "Teaching is already hard.",
    body: "Research on teacher decision-making suggests educators make thousands of decisions during a single school day, many of them about logistics, not instruction. Managing hundreds of tiny classroom tasks on top of actual teaching makes an already demanding profession even harder.",
  },
  closingHeadline: "Most teachers don't need more tools.",
  closingLine: "They need fewer things to remember.",
};

export const DEFINITION = {
  headline: "What Is Classroom Automation?",
  paragraphs: [
    "Classroom Automation is the practice of using technology to automatically manage classroom routines, transitions, timers, reminders, schedules, and recurring classroom tasks without requiring constant teacher intervention.",
    "Just as smart homes automate lights and thermostats, Classroom Automation automates repetitive classroom responsibilities.",
    "The goal is not replacing the teacher. The goal is removing unnecessary mental load so teachers can focus on teaching.",
  ],
  benefits: [
    { title: "Consistent routines", copy: "The same classroom procedures run the same way every day, even when you're pulled away or running behind." },
    { title: "Better classroom management", copy: "Predictable systems reduce off-task behavior and give students clear expectations without you repeating yourself." },
    { title: "Reduced stress", copy: "When the classroom handles logistics, you stop carrying a mental checklist of everything that still needs to happen." },
    { title: "Improved transitions", copy: "Classroom transitions become smoother when timers, music, and directions appear automatically at the right time." },
    { title: "More instructional time", copy: "Every minute spent restarting a timer or switching tabs is a minute not spent teaching. Automation gives that time back." },
    { title: "Less teacher workload", copy: "Set up your classroom workflow once, and let technology handle the repetitive parts of every period, every day." },
  ],
  comparisonVideoLabel:
    "Video needed: Side-by-side comparison of a traditional classroom (teacher manually starting timers, switching tabs, updating slides) versus an automated classroom (events firing on schedule without teacher intervention).",
  comparison: {
    traditional: {
      title: "Traditional classroom",
      items: [
        "Teacher starts every timer manually",
        "Directions repeated each period",
        "Transitions managed moment by moment",
        "Reminders depend on memory or sticky notes",
        "Screen content updated by hand",
        "Class changes require manual switching",
      ],
    },
    automated: {
      title: "Automated classroom",
      items: [
        "Timers start on schedule automatically",
        "Agendas and directions appear at set times",
        "Transitions run with music and countdowns",
        "Reminders display when students need them",
        "Content updates follow your daily routine",
        "Class periods advance without intervention",
      ],
    },
  },
};

export const WHY_MATTERS = {
  headline: "Why Classroom Automation Matters",
  intro:
    "Classroom Automation isn't a buzzword. It's a practical response to how classrooms actually work. Teachers juggle classroom routines, classroom management, transitions, and technology all day long. Automation connects those pieces into systems that run themselves.",
  subsections: [
    {
      id: "classroom-routines",
      title: "Classroom Routines",
      paragraphs: [
        "Strong classroom routines are the backbone of effective teaching. Morning meetings, bell work, station rotations, cleanup procedures. These patterns help students know what to expect and reduce wasted time at the start of every activity.",
        "The challenge is consistency. Even experienced teachers miss a step when the day gets hectic. A fire drill throws off the schedule. A parent conference runs long. Suddenly the routine you practiced for weeks falls apart.",
        "Classroom Automation keeps routines running even when you can't. Your morning message appears at 7:50 whether you're at your desk or greeting students at the door. The warm-up timer starts at 8:01 without you touching the computer. Routines become reliable because the system executes them, not because you remember every step under pressure.",
        "For teachers searching for ways to automate classroom routines or looking for automated classroom routines for teachers, the answer isn't working harder. It's building systems that execute your plan automatically.",
      ],
    },
    {
      id: "classroom-management",
      title: "Classroom Management",
      paragraphs: [
        "Effective classroom management depends on clear expectations and predictable structure. When students know what happens next and when, they spend less time off task and more time engaged.",
        "Traditional classroom management tools help you display information, but they still require you to operate them. You choose when to show the timer. You decide when to pull up the agenda. You manage the screen while also managing twenty-five students.",
        "Classroom management automation shifts that burden. Timers appear before students finish the previous task. Reminders about behavior expectations show up at transition points. Countdowns give students visual cues that cleanup time is ending. The classroom display becomes an active participant in management, not a passive screen waiting for your next click.",
        "Teachers exploring classroom management automation or classroom automation software often discover that the biggest management gains come not from new rules, but from removing the friction that makes consistent enforcement difficult.",
      ],
    },
    {
      id: "student-expectations",
      title: "Student Expectations",
      paragraphs: [
        "Students thrive on predictability. When the same things happen at the same times every day, anxiety decreases and engagement increases. They stop asking \"What are we doing?\" because the classroom already told them.",
        "Inconsistency sends the opposite message. When timers sometimes appear and sometimes don't, when agendas are updated sporadically, students learn that procedures are optional, and classroom behavior reflects that.",
        "Automated classroom systems create the consistency students need without adding to your workload. The present screen becomes a trusted source of information: what's happening now, what's next, and how much time remains.",
      ],
    },
    {
      id: "classroom-transitions",
      title: "Classroom Transitions",
      paragraphs: [
        "Classroom transitions are where instructional time quietly disappears. Moving from direct instruction to group work, from one activity to the next, from class period to class period. Each transition is a potential bottleneck.",
        "Teachers commonly use classroom timers and classroom countdowns during transitions, but starting them manually adds friction. By the time you've queued music, displayed the timer, and gotten students' attention, two or three minutes have passed.",
        "Classroom transition management through automation means transitions begin before you ask for them. Music starts. A countdown appears. The next agenda loads. Students learn to respond to the classroom systems, and you stay focused on teaching through the change rather than orchestrating it.",
        "Whether you call it classroom transition timer software or automated classroom transition management, the principle is the same: make transitions happen on schedule, not on demand.",
      ],
    },
    {
      id: "teacher-productivity",
      title: "Teacher Productivity",
      paragraphs: [
        "Teacher productivity isn't about doing more. It's about spending your energy on what only you can do. You are the expert on your students, your content, and your craft. You are not a timer operator, a slide clicker, or a reminder system.",
        "Every small task you automate is cognitive load you reclaim. Research on teacher burnout consistently points to workload and administrative burden as major factors. Classroom Automation directly addresses the death-by-a-thousand-clicks reality of modern teaching.",
        "Teachers looking for teacher productivity software or ways to reduce teacher workload often start with one automation (a single auto-start timer) and quickly realize how much mental space opens up when the classroom handles logistics.",
      ],
    },
    {
      id: "consistency",
      title: "Consistency",
      paragraphs: [
        "Consistency is the hidden multiplier in every successful classroom. It builds trust with students, reduces behavior issues, and creates an environment where learning can happen without constant renegotiation of procedures.",
        "The problem is that human teachers, especially tired, busy, overwhelmed teachers, are inconsistent by nature. Not because we don't care, but because we are managing too many variables at once.",
        "Classroom Automation provides consistency that doesn't depend on your energy level on a given Tuesday. The same routine runs on day one and day ninety. Your classroom organization becomes systematic rather than aspirational.",
      ],
    },
  ],
};

export const AUTOMATION_EXAMPLES = {
  headline: "Examples of Classroom Automation",
  intro:
    "Classroom Automation isn't one feature. It's a collection of automated systems working together, each one solving a specific daily problem so you don't have to.",
};

export const AUTOMATION_FEATURES = [
  {
    id: "auto-class-change",
    title: "Auto Class Change",
    problem:
      "Between periods, teachers manually switch to the next class's agenda, update the schedule display, and prepare materials. With five or six classes a day, that's dozens of manual switches, and easy to miss when you're helping a student or handling a hallway issue.",
    solution:
      "Auto Class Change automatically updates your classroom content based on your school schedule. When second period begins, the second period agenda, materials, and display appear without you clicking anything.",
    impact:
      "Students see the right content immediately. You walk in and teach instead of configuring your screen. Classroom transitions between periods become seamless.",
    videoLabel:
      "Video needed: School schedule advancing through multiple class periods automatically. Display updates with each class's agenda and materials without teacher input.",
    screenshotLabel:
      "Screenshot needed: Schedule view showing multiple classes with the present screen updating automatically as periods change.",
    seoParagraph:
      "Auto Class Change is an example of classroom automation because it automatically updates classroom content based on the school schedule, reducing teacher workload and improving classroom transitions.",
  },
  {
    id: "auto-timers",
    title: "Auto Timers",
    problem:
      "Teachers use classroom timers constantly for bell work, activities, tests, and cleanup. But every timer requires opening a tool, setting the duration, and starting it manually. Miss the start by thirty seconds and your entire lesson timing shifts.",
    solution:
      "Auto Timers launch at scheduled times as part of your daily routine. Set a five-minute warm-up timer to start at 8:01 AM, and it appears on your present screen automatically, every day, every period.",
    impact:
      "Students see time remaining without you saying a word. Activities stay on pace. You stop being the person who always has to remember to start the clock.",
    videoLabel:
      "Video needed: Timer appearing automatically on the classroom display at a scheduled time. Teacher continues teaching without touching the computer.",
    screenshotLabel:
      "Screenshot needed: Classroom present screen with an auto-started timer counting down during bell work or an activity.",
    seoParagraph:
      "Auto Timers deliver a classroom timer that starts automatically, with no tab switching, no manual setup. For teachers searching for an automated classroom timer or classroom timer that starts automatically, scheduled timers eliminate one of the most repeated tasks in teaching.",
  },
  {
    id: "auto-music",
    title: "Auto Music",
    problem:
      "Transition music helps students move between activities calmly and efficiently. But queuing a playlist, adjusting volume, and starting playback at the right moment adds another task to an already full transition.",
    solution:
      "Auto Music starts transition music at scheduled times in your routine. When cleanup begins, music plays. When it's time to settle in, a different track starts, all without you opening Spotify or YouTube.",
    impact:
      "Transitions feel smoother and more intentional. Students respond to auditory cues. You maintain a calm classroom atmosphere without becoming the DJ every period.",
    videoLabel:
      "Video needed: Transition music starting automatically during a classroom activity change. Students begin cleanup as music plays without teacher intervention.",
    screenshotLabel:
      "Screenshot needed: Present screen showing music playing during a transition with agenda updating in the background.",
    seoParagraph:
      "Auto Music supports classroom transitions and classroom routines by providing consistent auditory cues that signal activity changes, an often-overlooked part of classroom organization and classroom workflow.",
  },
  {
    id: "auto-reminders",
    title: "Auto Reminders",
    problem:
      "Important announcements (picture day, permission slips, early dismissal, test reminders) compete for attention during instructional time. Deliver them too early and students forget. Too late and it's chaos.",
    solution:
      "Auto Reminders display messages on your present screen at the times you schedule. A reminder about tomorrow's quiz appears at the end of class. A field trip notice shows during morning arrival.",
    impact:
      "Students receive information when it's most relevant. You stop interrupting lessons to make announcements. Your classroom organization improves because reminders happen systematically.",
    videoLabel:
      "Video needed: Reminder banner or modal appearing automatically on the classroom display at a scheduled time during class.",
    screenshotLabel:
      "Screenshot needed: Present screen with an auto-reminder about an upcoming deadline or event displayed prominently.",
    seoParagraph:
      "Auto Reminders are a core classroom organization tool within Classroom Automation, ensuring students see important information at the right moment without adding to your mental checklist.",
  },
  {
    id: "auto-happy-birthday",
    title: "Routine Birthday Steps",
    problem:
      "Celebrating student birthdays builds classroom community, but remembering every birthday while managing a full teaching load is nearly impossible. Birthdays get missed, and students notice.",
    solution:
      "Birthday steps in Automated Routines display birthday messages on your present screen, automatically using the roster you already maintain.",
    impact:
      "Students feel seen and valued. Classroom culture strengthens with minimal effort. You create moments of connection without adding another thing to remember.",
    videoLabel:
      "Video needed: Birthday message appearing automatically on the classroom display when a student enters class on their birthday.",
    screenshotLabel:
      "Screenshot needed: Present screen showing a student birthday celebration message with the student's name.",
    seoParagraph:
      "Routine birthday steps demonstrate how Classroom Automation supports classroom culture and classroom community by automating the small moments that make students feel known.",
  },
  {
    id: "auto-countdowns",
    title: "Auto Countdowns",
    problem:
      "Countdown timers create urgency and clarity: time until the bell, days until a test, minutes until dismissal. But setting them up manually for every occasion is tedious, and they're easy to forget.",
    solution:
      "Auto Countdowns appear on your present screen at scheduled times. A dismissal countdown starts at 2:45 PM. A days-until-test countdown appears each morning. They update automatically.",
    impact:
      "Students manage their time better with visible countdowns. End-of-day transitions become orderly. You eliminate the manual setup that makes countdowns feel like more work than they're worth.",
    videoLabel:
      "Video needed: Automated classroom countdown timer appearing on the display before dismissal or an event, counting down in real time.",
    screenshotLabel:
      "Screenshot needed: Present screen showing a large countdown timer for dismissal or an upcoming deadline.",
    seoParagraph:
      "Auto Countdowns provide an automated classroom countdown timer that starts on schedule, ideal for teachers who want classroom countdowns without manual setup every day.",
  },
  {
    id: "auto-navigation",
    title: "Auto Navigation",
    problem:
      "Complex lessons involve multiple materials (slides, videos, documents, activities). Moving between them manually breaks flow and pulls student attention to the screen instead of the content.",
    solution:
      "Auto Navigation advances through your lesson materials automatically based on your routine schedule. The warm-up slide appears at 8:00. Direct instruction materials load at 8:15. Exit ticket at 8:50.",
    impact:
      "Your classroom workflow runs smoothly from activity to activity. Classroom procedures become embedded in the technology rather than dependent on your memory. Instructional flow improves.",
    videoLabel:
      "Video needed: Present screen automatically advancing through lesson materials (slides, agenda, video) as scheduled events fire throughout a class period.",
    screenshotLabel:
      "Screenshot needed: Sequence of present screen states showing automatic content changes during a lesson.",
    seoParagraph:
      "Auto Navigation streamlines classroom workflow and classroom procedures by moving through lesson materials on schedule, reducing the manual steps that interrupt teaching momentum.",
  },
];

export const ROUTINE_BUILDER = {
  headline: "Build a Classroom That Runs Itself",
  intro:
    "Individual automations are powerful. The Automated Routine Builder combines them into complete classroom systems: a full daily classroom routine software experience that runs from arrival to dismissal.",
  body: [
    "Think of it as programming your ideal school day once, then letting it execute automatically every day after. You define what happens and when; Lesson Launchpad handles the rest.",
    "Morning music at 7:45. Morning message at 7:50. Class change at 8:00. Warm-up timer at 8:01. Reminder at 8:15. Dismissal countdown at 2:45. Each event connects to the next, creating a seamless classroom workflow.",
    "Teachers create routines once and allow them to run automatically every day, across every class period, every week, for the entire school year. That is the core promise of software for classroom routines and daily classroom routine software.",
  ],
  events: [
    { time: "7:45 AM", label: "Music starts" },
    { time: "7:50 AM", label: "Morning message appears" },
    { time: "8:00 AM", label: "Class changes" },
    { time: "8:01 AM", label: "Warm-up timer starts" },
    { time: "8:15 AM", label: "Reminder appears" },
    { time: "2:45 PM", label: "Dismissal countdown begins" },
  ],
  footerLine: "Set it once. Let it run all day. Every day.",
  videoLabel:
    "Video needed: Teacher building an automated routine in the Routine Builder, then showing that routine executing automatically throughout a full school day.",
  screenshotLabel:
    "Screenshot needed: Automated Routine Builder interface showing a timeline of scheduled events (music, timers, reminders, class changes) for a complete school day.",
};

export const DAY_IN_CLASS = {
  headline: "A Day in an Automated Classroom",
  intro:
    "What does Classroom Automation actually feel like? Here's a realistic walkthrough of a teacher's day when the classroom manages itself.",
  moments: [
    {
      time: "7:45 AM: Arrival",
      copy: "Students walk in to soft music already playing. The morning message is on screen: today's agenda, a reminder about the field trip form, and a birthday shout-out for Marcus. You greet students at the door instead of scrambling to open tabs.",
      automated: ["Music starts", "Morning message displays", "Birthday message appears"],
    },
    {
      time: "8:00 AM: Bell Work",
      copy: "The bell rings. Your warm-up activity appears automatically. A five-minute timer starts counting down. Students know exactly what to do because the routine is the same every day, and the classroom display confirms it.",
      automated: ["Warm-up content loads", "Timer starts automatically"],
    },
    {
      time: "8:20 AM: Direct Instruction",
      copy: "The timer ends. Transition music plays briefly. Your lesson slides appear on screen. You teach without touching the computer. A reminder about the quiz tomorrow appears in the corner at exactly the right moment.",
      automated: ["Transition music", "Lesson materials load", "Reminder displays"],
    },
    {
      time: "9:00 AM: Group Work",
      copy: "A fifteen-minute group work timer starts automatically. The agenda updates to show station assignments. When time is running low, a two-minute warning countdown appears. Students transition smoothly because the classroom told them when.",
      automated: ["Activity timer", "Agenda update", "Warning countdown"],
    },
    {
      time: "9:45 AM: Class Change",
      copy: "Second period begins. Auto Class Change updates the display with the new class's agenda, materials, and schedule. You don't click anything. The next class walks in to a screen that's already ready for them.",
      automated: ["Class change", "New agenda loads", "Period-specific materials"],
    },
    {
      time: "2:45 PM: Dismissal",
      copy: "A dismissal countdown appears on screen. Students see exactly how much time remains. Cleanup music plays. Reminders about homework and tomorrow's materials display automatically. You focus on final questions instead of managing the clock.",
      automated: ["Dismissal countdown", "Cleanup music", "End-of-day reminders"],
    },
  ],
  videoLabel:
    "Video needed: Full school day montage showing automated events firing at each stage: arrival, bell work, instruction, group work, class change, and dismissal, with minimal teacher interaction with the computer.",
};

export const VS_TRADITIONAL = {
  headline: "Classroom Automation vs Traditional Classroom Tools",
  intro:
    "Most classroom technology helps teachers manage classrooms. Classroom Automation helps classrooms manage themselves. Understanding the difference matters when you're evaluating classroom automation tools.",
  takeaway:
    "Most classroom software helps teachers manage classrooms. Classroom Automation helps classrooms manage themselves.",
  rows: [
    { feature: "Display content on screen", traditional: true, automated: true },
    { feature: "Run timers manually", traditional: true, automated: false },
    { feature: "Start timers automatically by schedule", traditional: false, automated: true },
    { feature: "Show reminders when you click", traditional: true, automated: false },
    { feature: "Display reminders at scheduled times", traditional: false, automated: true },
    { feature: "Update content between classes manually", traditional: true, automated: false },
    { feature: "Advance classes automatically", traditional: false, automated: true },
    { feature: "Play transition music on demand", traditional: true, automated: false },
    { feature: "Start music automatically during transitions", traditional: false, automated: true },
    { feature: "Build complete daily routines", traditional: false, automated: true },
    { feature: "Reduce ongoing teacher workload", traditional: false, automated: true },
  ],
  traditionalLabel: "Traditional Tools",
  automatedLabel: "Lesson Launchpad™",
};

export const FOUNDER_STORY = {
  headline: "Built by a Teacher Who Needed This",
  paragraphs: [
    "Lesson Launchpad didn't start as a product idea. It started as a frustration in a real classroom.",
    "After years of teaching, our founder knew the feeling intimately: standing at the front of the room, trying to teach, while simultaneously managing timers, switching between browser tabs, updating slides, remembering reminders, and keeping track of which class came next.",
    "Existing classroom technology could display information. Google Slides could show an agenda. YouTube could run a countdown. Spotify could play music. But nothing connected them. Nothing ran automatically. Every tool required another click, another tab, another moment of attention pulled away from students.",
    "The realization was simple: teachers don't need more tools to operate. They need their classroom to operate itself.",
    "That insight became Classroom Automation, and Lesson Launchpad became the platform built to deliver it. Not as a feature bolted onto presentation software, but as the entire purpose of the product.",
    "Today, Lesson Launchpad is defining the Classroom Automation category for K-12 teachers: a classroom automation platform where routines, transitions, timers, reminders, and classroom management systems work together automatically, so teachers can do what they became teachers to do.",
  ],
};

export const FAQ_ITEMS = [
  {
    id: "what-is",
    question: "What is Classroom Automation?",
    answer:
      "Classroom Automation is the practice of using technology to automatically manage classroom routines, transitions, timers, reminders, schedules, and recurring classroom tasks without requiring constant teacher intervention. Instead of manually starting timers or updating screens, teachers set up automated systems once and let them run throughout the school day.",
  },
  {
    id: "how-does-it-work",
    question: "How does Classroom Automation work?",
    answer:
      "Teachers configure their daily schedule, routines, and automations in a platform like Lesson Launchpad. Events (timers, reminders, music, class changes, content updates) are scheduled to fire at specific times. During the school day, the present screen executes those events automatically without teacher input.",
  },
  {
    id: "classroom-management",
    question: "Can Classroom Automation improve classroom management?",
    answer:
      "Yes. Consistent, automated routines give students clear expectations and reduce off-task behavior. Timers, countdowns, and reminders appear at predictable times, helping students self-manage and giving teachers more time to focus on instruction rather than logistics.",
  },
  {
    id: "classroom-routines",
    question: "Can Classroom Automation help with classroom routines?",
    answer:
      "Absolutely. Classroom Automation is designed specifically for classroom routines. Teachers build daily routines once: morning messages, bell work timers, transition music, dismissal countdowns, and the system runs them automatically every day across every class period.",
  },
  {
    id: "vs-presentation",
    question: "What is the difference between Classroom Automation and classroom presentation software?",
    answer:
      "Classroom presentation software helps you display content when you click. Classroom Automation displays and changes content on a schedule. Timers start, music plays, classes advance, and reminders appear without manual intervention. Presentation tools require operation; automation platforms run themselves.",
  },
  {
    id: "reduce-workload",
    question: "Can Classroom Automation reduce teacher workload?",
    answer:
      "Yes. By automating repetitive tasks like starting timers, switching between classes, displaying reminders, and managing transitions, Classroom Automation removes hundreds of small daily decisions from a teacher's mental load, freeing time and energy for actual teaching.",
  },
  {
    id: "auto-timers",
    question: "How do automated classroom timers work?",
    answer:
      "Automated classroom timers are scheduled as part of your daily routine. You set the duration and start time once. For example, a five-minute warm-up timer at 8:01 AM. Each day, the timer appears and starts counting down automatically on your present screen at that time.",
  },
  {
    id: "what-can-automate",
    question: "What classroom tasks can be automated?",
    answer:
      "Common automations include timers, countdowns, reminders, transition music, class changes, birthday messages, agenda updates, lesson material navigation, and complete daily routines. Any recurring classroom task that happens at a predictable time is a candidate for automation.",
  },
];

export const FINAL_CTA = {
  headline: "Once You've Experienced Classroom Automation, It's Hard to Imagine Teaching Without It",
  paragraphs: [
    "You became a teacher to teach.",
    "Not to constantly manage timers, reminders, transitions, schedules, and routines.",
    "Classroom Automation gives you those moments back.",
  ],
  primaryCta: "Start Free Trial",
  secondaryCta: "Watch Classroom Automation in Action",
};

export const DEMO_MODAL = {
  title: "Watch Classroom Automation in Action",
  label:
    "Video needed: 90-second overview of Classroom Automation in Lesson Launchpad, showing automated timers, class changes, reminders, music, and a complete daily routine running without teacher intervention.",
};

export const RELATED_CLUSTER_LINKS = {
  headline: "More ways to simplify classroom routines",
  description:
    "Explore tools and strategies for smoother transitions, clearer expectations, and less repeated teacher management.",
  links: [
    { label: "How to Automate Classroom Routines", href: "/classroom-routine-builder", note: null },
    { label: "Classroom Transition Management", href: "/classroom-transition-timer", note: null },
    { label: "Classroom Timer Software for Teachers", href: "/classroom-timer", note: null },
    { label: "Classroom Noise Meter", href: "/classroom-noise-meter", note: null },
    { label: "Classroom Automation vs ClassroomScreen", href: "/compare/classroomscreen-vs-lesson-launchpad", note: null },
    { label: "Classroom Procedures That Save Time", href: "/daily-agenda-template-for-teachers", note: null },
    { label: "Reduce Teacher Workload Through Automation", href: "/classroom-schedule-display", note: null },
    { label: "Teacher Productivity Systems", href: "/bell-ringer-timer", note: null },
  ],
};
